import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Skeleton,
  Stack,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import React, { Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { IoChevronForward, IoEye, IoEyeOff } from 'react-icons/io5'
import { logger } from 'src/shared/Logger'
import * as Yup from 'yup'
import { Area } from '../../domain/area'
import useRegisterElegibleUser from '../../hooks/useRegisterElegibleUser'
import ValidatePassword from '../ValidatePassword'

export const LOGIN_REGISTER_FORM = Object.freeze({
  password: 'login-register-pwd',
  approve: 'login-register-approve-checkbox',
  submitButton: 'login-register-submit-btn',
})

const RegisterForm = ({
  register,
  elegibleUser,
}: {
  elegibleUser: {
    user: { email: string; name: string; area: { id: number } }
    areas: Area[]
    isLoading: boolean
    sending: boolean
  }
  register: (data: { name: string; area: { id: number }; email: string; password: string }) => void
}) => {
  const [t] = useTranslation()
  const Validation = Yup.object().shape({
    name: Yup.string().trim().required(t('onboard.registration.validation.required')),
    areaId: Yup.string().required(t('onboard.registration.validation.required')),
    password: Yup.string()
      .min(8)
      .matches(/[a-z]/)
      .matches(/[A-Z]/)
      .matches(/[0-9]/)
      .matches(/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/),
  })
  const [showPassword, setShowPassword] = React.useState<boolean>(false)
  const [validPassword, setValidPassword] = React.useState<boolean>(false)
  const form = useFormik({
    initialValues: {
      name: elegibleUser.user.name,
      areaId: elegibleUser.user?.area?.id,
      password: '',
      isAccepted: false,
    },
    validationSchema: Validation,
    onSubmit: (values) => {
      logger.info('sending register data', values)
      register({
        email: elegibleUser.user.email,
        name: values.name,
        password: values.password,
        area: { id: elegibleUser?.user?.area?.id ?? elegibleUser?.areas?.[0]?.id },
      })
    },
  })

  useEffect(() => {
    form.setFieldValue('areaId', elegibleUser?.user?.area?.id ?? elegibleUser?.areas?.[0]?.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elegibleUser?.user?.area?.id])

  return (
    <>
      <form onSubmit={form.handleSubmit}>
        <Stack spacing={4}>
          <FormControl id='name' isInvalid={!!form.errors.name}>
            <FormLabel htmlFor='name' fontFamily='Mulish' fontWeight='extrabold'>
              {t('onboard.registration.Full Name')}
            </FormLabel>
            <Input
              type='text'
              name={'name'}
              onChange={form.handleChange}
              value={form.values.name || elegibleUser.user.name}
              placeholder={t('onboard.registration.Enter your Full Name')}
              w='100%'
            />
            <FormErrorMessage>{form.errors.name}</FormErrorMessage>
          </FormControl>
          {/* {elegibleUser.areas.length !== 0 ? (
            <FormControl id='areaId' isInvalid={!!(form.errors.areaId && form.touched.areaId)}>
              <FormLabel htmlFor='areaId' fontFamily='Mulish' fontWeight='extrabold'>
                {t('onboard.registration.Area')}
              </FormLabel>
              <Select
                type='text'
                height={'48px'}
                name={'areaId'}
                placeholder={t('onboard.registration.Select your current area')}
                w='100%'
                onChange={form.handleChange}
                value={form.values.areaId || elegibleUser.user?.area?.id}
                // style={{ color: true ? '#8C8C8C' : '#313E4C' }}
              >
                {elegibleUser.areas.map((area: Area) => {
                  return (
                    <option value={area.id} key={area.id}>
                      {area.name}
                    </option>
                  )
                })}
              </Select>
              <FormErrorMessage>{form.errors.areaId}</FormErrorMessage>
            </FormControl>
          ) : null} */}
          <FormControl id='password' isInvalid={!!(form.errors.password && form.touched.password)}>
            <FormLabel htmlFor='password' fontFamily='Mulish' fontWeight='extrabold'>
              {t('onboard.registration.Password')}
            </FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete='new-password'
                id='new-password'
                placeholder={t('onboard.registration.Create Password')}
                w='100%'
                name={'password'}
                onChange={form.handleChange}
                value={form.values.password}
                data-testid={LOGIN_REGISTER_FORM.password}
              />
              <InputRightElement width='4.5rem'>
                <IconButton
                  as='div'
                  cursor='pointer'
                  icon={showPassword ? <IoEyeOff /> : <IoEye />}
                  backgroundColor='transparent'
                  _hover={{
                    bg: 'transparent',
                  }}
                  _active={{
                    bg: 'transparent',
                  }}
                  _focus={{
                    bg: 'transparent',
                  }}
                  fontSize='xl'
                  color='gray.700'
                  size='sm'
                  fontFamily='Poppins'
                  fontWeight='bold'
                  aria-label='Show/hide password'
                  onClick={() => setShowPassword(!showPassword)}
                  type='button'
                ></IconButton>
              </InputRightElement>
            </InputGroup>
          </FormControl>
        </Stack>
        <Suspense fallback='loading...'>
          <Box my={'15px'}>
            <ValidatePassword
              password={form.values.password}
              onChange={(isValid) => {
                setValidPassword(isValid)
              }}
            />
          </Box>
        </Suspense>
        <Checkbox
          name={'isAccepted'}
          isRequired
          colorScheme='green'
          fontSize='sm'
          onChange={form.handleChange}
          isChecked={form.values.isAccepted}
          data-testid={LOGIN_REGISTER_FORM.approve}
        >
          {t('onboard.registration.I have read and agreed with the')}
          <Link
            href='https://educationjourney.zendesk.com/hc/pt-br/articles/4406545793555-TERMOS-E-CONDIÇÕES-GERAIS-DE-USO-'
            isExternal={true}
            color='blue'
          >
            {t('onboard.registration.Terms of Use')}
          </Link>
          {t('onboard.registration.and the')}{' '}
          <Link
            href='https://educationjourney.zendesk.com/hc/pt-br/articles/4406538324371'
            isExternal={true}
            color='blue'
          >
            {t('onboard.registration.Privacy Policy')}
          </Link>
          {t('onboard.registration.Info Sharing Disclaimer')}
        </Checkbox>
        <Button
          w='100%'
          rightIcon={<IoChevronForward />}
          type='submit'
          data-testid={LOGIN_REGISTER_FORM.submitButton}
          disabled={elegibleUser.sending || !form.values.isAccepted || !validPassword}
          marginTop={6}
          size='lg'
          isLoading={elegibleUser.sending}
        >
          {t('onboard.registration.Next Step')}
        </Button>
      </form>
    </>
  )
}

const UserRegisterForm = () => {
  const { elegibleUser, register } = useRegisterElegibleUser()
  if (elegibleUser.isLoading)
    return (
      <Stack>
        <Skeleton height='20px' />
        <Skeleton height='20px' />
        <Skeleton height='20px' />
      </Stack>
    )
  return (
    <>
      <RegisterForm elegibleUser={elegibleUser} register={register} />
    </>
  )
}

export default UserRegisterForm
